import InfoIcon from '@mui/icons-material/Info';
import { Checkbox, Stack, Tooltip, Typography } from '@mui/material';
import {
  useGridApiContext,
  type GridColDef,
  type GridPreProcessEditCellProps,
  type GridRenderCellParams,
  type GridRenderEditCellParams
} from '@mui/x-data-grid';
import type { ChangeEvent } from 'react';
import {
  existeAreteSiiniga,
  existeAreteTemporal
} from '../../api-client/pre-compras/pre-compras.api';
import TooltipInputCell from '../ui/TooltipInputCell';
import type { AnimalPreCompraRow } from './AnimalPreCompraRow';

export const colDefinitions: GridColDef<AnimalPreCompraRow>[] = [
  {
    field: 'areteSiiniga',
    headerAlign: 'center',
    renderHeader: () => (
      <Stack direction="row" spacing={0.5}>
        <Typography fontWeight="bold">SIINIGA</Typography>
        <Tooltip title="El arete SIINIGA debe tener 10 números.">
          <InfoIcon color="info" fontSize="small" />
        </Tooltip>
      </Stack>
    ),
    width: 130,
    editable: true,
    preProcessEditCellProps: async (params: GridPreProcessEditCellProps) => {
      const shouldValidate = params.hasChanged && params.props.value;
      if (!shouldValidate) {
        return { ...params.props, error: undefined };
      }

      if (!params.row.areteTemporal && !params.props.value) {
        return {
          ...params.props,
          error: 'El arete SIINNIGA es requerido si el arete temporal no está presente'
        };
      }

      const esFormatoValido = /^\d{10}$/.test(params.props.value);
      if (!esFormatoValido) {
        return { ...params.props, error: 'Formato de arete inválido' };
      }

      const existeAreteEnDb = await existeAreteSiiniga(params.props.value, params.row.folio);
      if (existeAreteEnDb) {
        return { ...params.props, error: 'Este arete SIINIGA ya existe en otra pre-compra' };
      }

      return { ...params.props, error: undefined };
    },
    renderEditCell: (props: GridRenderEditCellParams) => {
      return <TooltipInputCell {...props} maxLength={10} />;
    },
    valueFormatter: (value) => {
      const valueStr = String(value ?? '');
      return valueStr.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3');
    }
  },
  {
    field: 'historialSiiniga',
    type: 'actions',
    headerAlign: 'center',
    width: 60,
    cellClassName: 'actions'
  },
  {
    field: 'areteTemporal',
    headerAlign: 'center',
    renderHeader: () => (
      <Stack direction="row" spacing={0.5}>
        <Typography fontWeight="bold">Temporal</Typography>
        <Tooltip title="El arete temporal debe tener el formato A0000.">
          <InfoIcon color="info" fontSize="small" />
        </Tooltip>
      </Stack>
    ),
    width: 130,
    editable: true,
    preProcessEditCellProps: async (params: GridPreProcessEditCellProps) => {
      const shouldValidate = params.hasChanged && params.props.value;
      if (!shouldValidate) {
        return { ...params.props, error: undefined };
      }

      if (!params.row.areteSiiniga && !params.props.value) {
        return {
          ...params.props,
          error: 'El arete temporal es requerido si el arete SIINIGA no está presente'
        };
      }

      const esFormatoValido = /^[a-zA-Z]{1}[0-9]{1,3}$/.test(params.props.value);
      if (!esFormatoValido) {
        return { ...params.props, error: 'Formato de arete inválido' };
      }

      const existeAreteEnDb = await existeAreteTemporal(params.props.value, params.row.folio);
      if (existeAreteEnDb) {
        return { ...params.props, error: 'Este arete temporal ya existe en otra pre-compra' };
      }

      return { ...params.props, error: undefined };
    },
    renderEditCell: (props: GridRenderEditCellParams) => {
      return <TooltipInputCell {...props} maxLength={5} />;
    },
    valueFormatter: (value) => {
      const valueStr = String(value ?? '');
      return valueStr.replace(/([a-zA-Z]{1})([0-9]{1,3})/, '$1-$2');
    }
  },
  {
    field: 'historialTemporal',
    type: 'actions',
    headerAlign: 'center',
    headerName: '',
    width: 60,
    cellClassName: 'actions'
  },
  {
    field: 'tienePapeles',
    headerAlign: 'center',
    headerName: 'Papeles',
    width: 100,
    type: 'boolean',
    editable: true,
    renderCell: (params: GridRenderCellParams) => {
      return <Typography>{params.value === true ? 'SÍ' : 'NO'}</Typography>;
    },
    renderEditCell: ({ id, value, field }: GridRenderEditCellParams) => {
      const api = useGridApiContext();
      const handleChange = (event: ChangeEvent<any>) => {
        const newValue = event.target.checked;
        api.current.setEditCellValue({ id, field, value: newValue }, event);
        if (!newValue) {
          api.current.setEditCellValue({ id, field: 'reemo', value: '' }, event);
        }
      };
      return <Checkbox checked={value} onChange={handleChange} />;
    }
  },
  {
    field: 'reemo',
    headerAlign: 'center',
    renderHeader: () => (
      <Stack direction="row" spacing={0.5}>
        <Typography fontWeight="bold">REEMO</Typography>
        <Tooltip title="REEMO es requerido si el animal tiene papeles">
          <InfoIcon color="info" fontSize="small" />
        </Tooltip>
      </Stack>
    ),
    width: 180,
    editable: true,
    renderEditCell: (props: GridRenderEditCellParams) => {
      const value = props.row.tienePapeles;
      return value ? <TooltipInputCell {...props} maxLength={16} /> : <Typography />;
    },
    preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
      const shouldValidate = params.hasChanged && params.row.tienePapeles;
      if (!shouldValidate) {
        return { ...params.props, error: undefined };
      }

      const error = !params.props.value
        ? 'REEMO es requerido si el animal tiene papeles'
        : undefined;
      return { ...params.props, error: error };
    }
  },
  {
    field: 'idTipoGanado',
    headerAlign: 'center',
    headerName: 'Tipo',
    width: 180,
    editable: true
  },
  {
    field: 'kgCompra',
    align: 'right',
    headerAlign: 'center',
    headerName: 'Kg. Compra',
    width: 115,
    type: 'number',
    editable: true
  },
  {
    field: 'kgLlegada',
    align: 'right',
    headerAlign: 'center',
    headerName: 'Kg. Llegada',
    width: 115,
    type: 'number',
    editable: true
  },
  {
    field: 'diferencialKg',
    headerAlign: 'center',
    headerName: 'Diferencial Kg.',
    width: 120,
    type: 'number',
    editable: false,
    valueGetter: (_, row) => {
      return row.kgCompra - row.kgLlegada;
    }
  },
  {
    field: 'diferencialPorcentual',
    headerAlign: 'center',
    headerName: 'Diferencial %',
    width: 115,
    type: 'number',
    editable: false,
    valueGetter: (_, row) => {
      return 100 - (row.kgLlegada * 100) / row.kgCompra;
    }
  },
  {
    field: 'actions',
    type: 'actions',
    headerAlign: 'center',
    headerName: 'Acciones',
    width: 100,
    cellClassName: 'actions'
  }
];
